import dayjs from 'dayjs';

import {httpDelete, httpGet, httpPost, httpPut} from './http';
import {getActiveMembership} from '../lib/activeMember';
import {userIsStaff} from '../lib/role';
import {messageType, showMessages} from './message';
import {endTimeCannotBeInThePast, startTimeCannotBeInThePast, startTimeHasToBeEarlierThanEndTime} from './text';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import {store} from '../store/store';
import {apiSlice} from '../store/api/apiSlice';

const bookingUrl = window.env.BACKEND_API_URL + '/bookings';
dayjs.extend(isSameOrAfter);

// region CRUD
const getBookings = async ({
                               page = 1,
                               pageSize = 10,
                               searchType = 'contained_by',
                               startedAt = null,
                               endedAt = null,
                               state = null,
                               descSort = 0
                           }, signal) => {
    page = encodeURIComponent(page);
    pageSize = encodeURIComponent(pageSize);
    searchType = encodeURIComponent(searchType);
    descSort = encodeURIComponent(descSort);

    let queryString = `?page=${page}&per_page=${pageSize}&search_type=${searchType}&desc_sort=${descSort}`;

    if (startedAt) {
        startedAt = encodeURIComponent(startedAt);
        queryString += `&booking_started_at=${startedAt}`;
    }
    if (endedAt) {
        endedAt = encodeURIComponent(endedAt);
        queryString += `&booking_ended_at=${endedAt}`;
    }
    if (state) {
        state = encodeURIComponent(state);
        queryString += `&state=${state}`;
    }

    if (userIsStaff()) {
        const membershipID = encodeURIComponent(getActiveMembership().id);
        queryString += `&membership_id=${membershipID}`;
    }

    const bookingIndexUrl = queryString ? `${bookingUrl}/${queryString}` : bookingUrl;
    return await httpGet(bookingIndexUrl, signal);
};

const createBooking = async ({deploymentID, startedAt, bookingType, endedAt}, signal) => {
    // Required parameters
    const body = {
        'deployment_id': deploymentID,
        'started_at': startedAt.startOf('minute').format('YYYY-MM-DD HH:mm:ssZZ'),
        'type': bookingType,
        'membership_id': getActiveMembership().id,
    };

    if (endedAt) {
        body['ended_at'] = endedAt.startOf('minute').format('YYYY-MM-DD HH:mm:ssZZ');
    }

    return await httpPost(bookingUrl, body, signal);
};

const getBooking = async (bookingID, signal) => {
    const bookingViewUrl = `${bookingUrl}/${bookingID}`;

    return await httpGet(bookingViewUrl, signal);
};

const updateBooking = async (bookingID, {...rest}, signal) => {
    const bookingUpdateURL = `${bookingUrl}/${bookingID}`;

    // Invalidate deployment cache
    store.dispatch(apiSlice.util.invalidateTags(['Deployments', {type: 'Booking', id: bookingID}]));

    return await httpPut(bookingUpdateURL, {...rest}, signal);
};

const cancelBooking = async (bookingID, signal) => {
    const bookingCancelUrl = `${bookingUrl}/${bookingID}`;

    return await httpDelete(bookingCancelUrl, signal);
};

const discountBooking = async (bookingID, {portion, type, percent, amount}, signal) => {
    const bookingDiscountURL = `${bookingUrl}/${bookingID}/discount`;

    const body = {
        portion,
        type,
        percent,
        amount,
    };

    return await httpPost(bookingDiscountURL, body, signal);
}
// endregion

const batchUpdate = async (data, signal) => {
    const batchUrl = `${bookingUrl}/batch`;
    const body = {data};

    return await httpPut(batchUrl, body, signal);
};

const getBookingOwner = async (bookingID, signal) => {
    const bookingOwnerUrl = `${bookingUrl}/${bookingID}/booking_owner`;

    return await httpGet(bookingOwnerUrl, signal);
};

const getBookingEstimatedCost = async (startTime, endTime, signal) => {
    startTime = encodeURIComponent(startTime.startOf('minute').format('YYYY-MM-DD HH:mm:ssZZ'));
    endTime = encodeURIComponent(endTime.startOf('minute').format('YYYY-MM-DD HH:mm:ssZZ'));
    let queryString = `${bookingUrl}/estimated_costs?started_at=${startTime}&ended_at=${endTime}`;

    if (userIsStaff()) {
        const membershipID = encodeURIComponent(getActiveMembership().id);
        queryString += `&membership_id=${membershipID}`;
    }

    const bookingEstimatedCostUrl = `${queryString}`;

    return await httpGet(bookingEstimatedCostUrl, signal);
}

const getBookingDeploymentHistory = async (bookingID, signal) => {
    const bookingDeploymentHistoryUrl = `${bookingUrl}/getBookingDeploymentHistory?booking_id=${bookingID}`;

    return await httpGet(bookingDeploymentHistoryUrl, signal);
}

const getBookingIncrement = () => {
    const bookingConfig = apiSlice.endpoints.getBookingConfig.select()(
        store.getState()
    );
    return bookingConfig.data?.booking_increment;
};

const getMinBookingLength = () => {
    const bookingConfig = apiSlice.endpoints.getBookingConfig.select()(
        store.getState()
    );
    return bookingConfig.data?.min_booking_length;

};

const getDistanceUnit = () => {
    const bookingConfig = apiSlice.endpoints.getBookingConfig.select()(
        store.getState()
    );
    let unit = bookingConfig.data?.distance_unit;
    if (unit === null) {
        unit = 'km';
    }
    return unit;
};

const hasDistance = (costs) => {
    if (costs.length > 0) {
        const distanceObj = costs.find(cost => cost.type === 'distance');
        return distanceObj !== undefined && distanceObj.amount > 0;
    } else {
        return false
    }
}

const isEstimatedCost = (state, costs, now, endedAt) => {
    return !(state === 'CANCELLED' || (now.isAfter(endedAt) && hasDistance(costs)))
}

const roundToBookingIncrement = (timestamp, roundingMethod = 'nearest') => {
    const bookingIncrementMinutes = Number(getBookingIncrement()) / 60;
    let roundedMinute;

    switch (roundingMethod) {
        case 'up':
            roundedMinute = Math.ceil(timestamp.minute() / bookingIncrementMinutes) * bookingIncrementMinutes;
            break;
        case 'down':
            roundedMinute = Math.floor(timestamp.minute() / bookingIncrementMinutes) * bookingIncrementMinutes;
            break;
        case 'nearest':
            roundedMinute = Math.round(timestamp.minute() / bookingIncrementMinutes) * bookingIncrementMinutes;
            break;
        default:
            roundedMinute = Math.round(timestamp.minute() / bookingIncrementMinutes) * bookingIncrementMinutes;
            break;
    }

    return timestamp.clone().minute(roundedMinute).second(0).millisecond(0);
};

const getGracePeriod = () => {
    let gracePeriod = localStorage.getItem('grace_period');

    return gracePeriod ? parseInt(gracePeriod) : 0;
};

const isWithinGracePeriod = (createdAt, now = dayjs()) => {
    // Don't add .tz() to these.
    if (!(createdAt instanceof dayjs)) {
        createdAt = dayjs(createdAt);
    }
    if (!(now instanceof dayjs)) {
        now = dayjs(now);
    }

    const bookingConfig = apiSlice.endpoints.getBookingConfig.select()(
        store.getState()
    );
    const gracePeriod = bookingConfig.data?.grace_period;
    const gracePeriodEnd = createdAt.clone().add(gracePeriod, 's');

    return now.isBefore(gracePeriodEnd);
};

const dstOffSetDateCheck = ({start, end, isStart, format}) => {
    // Return early if either start or end is missing.
    if (!start || !end) return [start, end];

    // In the small edge case of a user making a booking that overlaps when Daylight Savings Time starts/ends.
    // When editing the booking, the newTime value retains its UTC offset permanently.
    // To avoid this, correct the newTime with the dayjs utcOffset method.
    let newTime = roundToBookingIncrement(isStart ? start : end, 'up');

    // Correct the time offset if Daylight Savings Time applies.
    const correctTimeOffSet = dayjs(newTime, format).tz().utcOffset();
    if (newTime.utcOffset() !== correctTimeOffSet) {
        newTime = newTime.utcOffset(correctTimeOffSet, true).tz();
    }

    // Assign the corrected time to either start or end based on isStart.
    if (isStart) {
        start = newTime;
        // Ensure end is not before start (adjust for DST).
        if (start.isAfter(end, 'day')) {
            end = end.year(start.year()).day(start.day());
        }
    } else {
        end = newTime;
        // Ensure start is not after end (adjust for DST).
        if (start.isAfter(end, 'day')) {
            start = start.year(end.year()).day(end.day());
        }
    }

    return [start.tz(), end.tz()];
};

const checkStartTimeBeforeEndTime = (newStartedAt, newEndedAt) => {
    if (newStartedAt.isBefore(newEndedAt)) {
        return true;
    }

    showMessages(startTimeHasToBeEarlierThanEndTime, messageType.error);
    return false;
};

const checkStartTimeFuture = (newStartedAt, validStart) => {
    let minStart = roundToBookingIncrement(validStart, 'down');
    if (newStartedAt.isSameOrAfter(minStart)) {
        return true;
    }

    showMessages(startTimeCannotBeInThePast, messageType.error);
    return false;
};

const checkEndTimeIsValid = (newEndedAt, oldEndedAt, validEnd) => {
    if (!roundToBookingIncrement(newEndedAt, 'up').isAfter(validEnd)) {
        showMessages(endTimeCannotBeInThePast, messageType.error);
        return false;
    }

    return true;
};

const bookingInfo = {
    type: {
        existing: {
            internalName: 'existingBooking',
            displayName: 'Unavailable'
        },
        new: {
            internalName: 'newBooking',
            displayName: 'New Booking',
        }
    },
    history: {
        bookingMade: 'Booking made',
        currentBookingInfo: 'Current booking information',
        bookingChange: 'Booking change',
    },
    warning: {
        invalidSelection: 'invalid',
    },
    error: {
        invalidSelection: 'invalid',
        availabilityChange: 'availability',
        invalidAction: 'invalidAction',
    },
};

export {
    getBookings,
    createBooking,
    getBooking,
    getBookingEstimatedCost,
    getBookingDeploymentHistory,
    cancelBooking,
    updateBooking,
    discountBooking,
    batchUpdate,
    getBookingOwner,
    getBookingIncrement,
    getMinBookingLength,
    getDistanceUnit,
    getGracePeriod,
    isWithinGracePeriod,
    roundToBookingIncrement,
    hasDistance,
    isEstimatedCost,
    checkStartTimeBeforeEndTime,
    checkStartTimeFuture,
    checkEndTimeIsValid,
    bookingInfo,
    dstOffSetDateCheck
};
